import React from 'react';
import styles from './text-field.scss';
import commonStyles from '../common.scss';

import FieldNote from '../field-note';
import { FormField } from '../form-field';
import FieldLine from 'ui/form/field-line';

import { validateEmail, validatePhoneNumber, validateUrl } from './utils.js';
import { TextFieldProps } from 'ui/form/fields';
import { ICON_ERROR } from 'icons';
import { REQUIRED } from 'ui/form/constants';
import { isValue } from 'ui/form/utils';

interface TextFieldState {
  focus: boolean;
  internalValue: string;
  value: string;
}
const TYPE_HAS_VALUE = {
  date: 1,
  'datetime-local': 1,
  time: 1
};

let id = 1;

export default class TextField extends FormField<TextFieldProps, TextFieldState> {
  static get defaultValue() {
    return '';
  }

  static getDerivedStateFromProps({ value }: TextFieldProps, { value: prevValue }: TextFieldState) {
    let ret = null;
    if (value !== prevValue) {
      ret = {
        internalValue: value || value === 0 ? value : '',
        value
      };
    }

    return ret;
  }

  static getErrors(props: TextFieldProps, formData): string | false {
    const value = isValue(props.value) ? props.value : (formData || {})[props.name];
    const { email, maxLength, minLength, phone, regErrorMsg, regexp, required, url } = props;

    if (required && (!value || !value.length)) return REQUIRED;

    if (minLength && (value || '').length < minLength) {
      return `Must be min ${minLength} characters`;
    }

    if (isValue(value)) {
      if (maxLength && value.length > maxLength) {
        return `Must be max ${maxLength} characters`;
      }

      if (regexp) {
        const reg: RegExp = typeof regexp === 'string' ? new RegExp(regexp) : regexp;

        if (!reg.test(value)) {
          return regErrorMsg || 'Input is invalid';
        }
      }

      if (email && !validateEmail(value)) {
        return regErrorMsg || 'Email is invalid';
      }

      if (phone && !validatePhoneNumber(value)) {
        return regErrorMsg || 'Phone number is invalid';
      }

      if (url && !validateUrl(value)) {
        return regErrorMsg || 'Url is invalid';
      }
    }

    return false;
  }

  state = { focus: false, internalValue: '', changed: true };
  id = 'text-' + id++;
  input: HTMLInputElement | HTMLTextAreaElement;
  validateTimeout: Number;

  componentDidMount() {
    this.resize();
  }

  componentWillUnmount() {
    this.handleValidate();
  }

  componentDidUpdate() {
    this.resize();
  }

  resize() {
    if (this.props.multiline && this.input) {
      this.input.style.height = 'auto';
      this.input.style.height = this.input.scrollHeight + 'px';
    }
  }

  focus = () => {
    if (this.input) this.input.focus({ preventScroll: true });
  };

  validate(onlyTouched: boolean) {
    const internalError = TextField.getErrors(this.props);
    this.setState({ internalError });

    return !internalError;
  }

  handleBlur = e => {
    if (this.props.noValidation) {
      this.setState({ focus: false });
      if (this.props.onBlur) this.props.onBlur(e);
    } else {
      this.setState({ focus: false }, this.handleValidate);
      if (this.props.onBlur) this.props.onBlur(e);
    }
  };

  handleChange = (e: Event) => {
    if (this.props.noValidation) {
      const internalValue = e.currentTarget.value;
      this.setState({ internalValue });
      if (this.props.onChange) this.props.onChange(internalValue);
    } else {
      clearTimeout(this.validateTimeout);
      this.validateTimeout = setTimeout(this.handleValidate, 500);
      const internalValue = e.currentTarget.value;
      this.setState({ internalValue });
      if (this.props.onChange) this.props.onChange(internalValue);
    }
  };

  handleFocus = (e: FocusEvent) => {
    if (this.props.onFocus) this.props.onFocus(e);
    if (!e.defaultPrevented) this.setState({ focus: true });
  };

  handleValidate = () => {
    clearTimeout(this.validateTimeout);

    this.validate();
  };

  render() {
    const { internalValue, internalError, focus } = this.state;
    let {
      autoFocus,
      error = internalError,
      disabled,
      fullWidth,
      hideNote,
      help,
      icon,
      inputStyle,
      inputClassName,
      label,
      leftComponent,
      multiline,
      multilineRows,
      name,
      noAutocomplete,
      readOnly,
      required,
      style,
      tabIndex,
      placeholder,
      tiny,
      type
    } = this.props;

    if (!help && required) help = REQUIRED;
    let cl = [styles.root, commonStyles.field];
    if (focus) cl.push(styles.focus);
    if (error) cl.push(styles.error);
    if (leftComponent) cl.push(styles.leftComponentField);
    if (icon || error) cl.push(styles.hasIcon);
    if (internalValue || internalValue === 0 || placeholder || TYPE_HAS_VALUE[type]) {
      cl.push(styles.hasValue);
    }
    if (tiny) cl.push(styles.tiny);
    if (disabled) cl.push(styles.disabled);

    const inputProps = {
      name,
      readOnly,
      disabled,
      placeholder,
      autoComplete: !noAutocomplete ? 'on' : 'off',
      autoFocus,
      style: inputStyle,
      className: styles.text + (inputClassName ? ' ' + inputClassName : ''),
      value: internalValue,
      id: this.id,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      onChange: this.handleChange,
      onKeyDown: this.props.onKeyDown,
      onMouseDown: this.props.onMouseDown,
      tabIndex: tabIndex || 0,
      ref: ref => (this.input = ref)
    };

    if (multiline) {
      inputProps.rows = multilineRows || 1;
      cl.push(styles.multiline);
    } else {
      inputProps.type = type || 'text';
    }

    if (fullWidth) style = { ...(style || {}), width: '100%' };

    return (
      <div className={cl.join(' ')} onClick={this.focus} style={style}>
        <div className={tiny ? styles.tinyField : styles.field}>
          {leftComponent}
          <div className={styles.inputHolder}>
            {label && <label htmlFor={this.id}>{label}</label>}
            {React.createElement(multiline ? 'textarea' : 'input', inputProps)}
          </div>
          <FieldLine
            defaultColor={tiny ? '#4b4d54' : ''}
            open={focus}
            style={{ bottom: tiny ? 1 : -1 }}
          />
          {!tiny &&
            (icon || error) && <div className={styles.icon}>{error ? ICON_ERROR : icon}</div>}
        </div>
        {!tiny && !hideNote && <FieldNote note={help} error={error} />}
      </div>
    );
  }
}
